import { useEffect, useMemo, useState } from 'react';
import Main from 'pages/Main';
import Auth from 'pages/Auth';
import { useDispatch, useSelector } from 'react-redux';
import { removeItemAfterSignUp } from './utils/soldOut';
import GoogleTagWidget from 'components/GoogleTagWidget';
import { useHistory } from 'react-router-dom';
import ConfirmProductActions from 'components/ConfirmProductActions';
import UnpublishedInventoryChangesModal from 'components/Confirm/UnpublishedInventoryChangesModal';
import {
  EventStatusesEnum,
  EventTypeEnum,
  ModalOpenStatusEnum,
  SET_MODAL_ACTIONS,
  SET_PRODUCT_EVENTS,
  SET_PRODUCT_EVENTS_NEXT_LOCATION,
} from 'components/ConfirmProductActions/constants';
import { v4 as uuidv4 } from 'uuid';
import { createCachingEvent } from 'components/ConfirmProductActions/service';
import { linkedWithProvider } from 'helpers/linkedWithProvider';
import useProviderConfig from 'hooks/useProviderConfig';
import useSetupInterceptorAccessDenied from 'components/AccessDenied';
import { selectCurrentBusiness } from 'redux/selectors';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';

function App() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { authorized, data } = useSelector(({ user }) => user);
  const { menu } = useSelector(({ menu }) => menu);
  const { menu: mainMenu } = useSelector((store) => store);
  const productEvents = useSelector((store) => store.productEvents);
  const currentBusiness = useSelector(selectCurrentBusiness);
  const { providers } = useProviderConfig();
  const userId = data?._id || '';

  // Nested-menu businesses don't use the legacy provider-menu sync flow, so the
  // inventory "Unpublished Inventory Changes" navigation guard must never fire.
  const isRestaurantWithNestedMenu = currentBusiness?.businessTypes?.includes(
    BUSINESS_TYPE_CATEGORY.restaurantWithNestedMenu
  );
  const [nextLoc, setNextLoc] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [inventoryNextLocation, setInventoryNextLocation] = useState(null);
  const categoriesBulk = menu?.categories?.find(
    (cat) => !cat?.bulkInfo?.isSaved
  );
  const productsBulk = menu?.products?.find(
    (product) => !product?.bulkInfo?.isSaved
  );
  const menuBulk = !menu?.bulkInfo?.isSaved;
  const isBulked = menuBulk || categoriesBulk || productsBulk;
  const menuLength = menu?.categories?.length;

  useSetupInterceptorAccessDenied();

  const linked = useMemo(() => {
    return (
      menu.connectedProviders &&
      linkedWithProvider(menu.connectedProviders, providers)
    );
  }, [menu]);

  // Check if there are unsaved inventory changes
  const hasInventoryUnsavedChanges = useMemo(() => {
    if (isRestaurantWithNestedMenu) return false;
    return !mainMenu?.loading && !(menu?.bulkInfo?.isSaved && linked);
  }, [
    mainMenu?.loading,
    menu?.bulkInfo?.isSaved,
    linked,
    isRestaurantWithNestedMenu,
  ]);

  const historyBlock = (nextLocation) => {
    // Skip blocking if user already confirmed (bypass flag in state)
    if (nextLocation.state?.bypassInventoryBlock) {
      console.log('✅ Bypassing inventory block - user confirmed');
      return true;
    }

    // Handle inventory navigation blocking
    if (
      history.location.pathname.includes('/inventory') &&
      hasInventoryUnsavedChanges &&
      !nextLocation.pathname.includes('/inventory')
    ) {
      console.log('🚨 Blocking navigation from inventory with unsaved changes');
      setInventoryNextLocation(nextLocation);
      setShowInventoryModal(true);
      return false;
    }

    // Handle menu navigation blocking
    if (
      isBulked &&
      history.location.pathname.includes('menu') &&
      menuLength &&
      linked
    ) {
      setNextLoc(nextLocation?.pathname);
      dispatch({
        type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
        payload: `${nextLocation.pathname}${nextLocation.search}`,
      });

      if (
        Object.prototype.hasOwnProperty.call(menu, 'bulkInfo') &&
        isBulked &&
        !nextLocation.pathname.includes('menu')
      ) {
        createCachingEvent({
          uuid: uuidv4(),
          status: EventStatusesEnum.created,
          type: EventTypeEnum.notice,
          metadata: {
            menuIds: [],
            source: 'menu',
          },
          tag: 'unpushedChanges',
          editorId: userId,
          acknowledgement: 'manual',
          serviceSource: 'mms',
        }).then((res) => {
          const { data } = res;
          dispatch({
            type: SET_PRODUCT_EVENTS,
            payload: [data?.data],
          });
          dispatch({
            type: SET_MODAL_ACTIONS,
            payload: ModalOpenStatusEnum.opened,
          });
        });

        return false;
      } else {
        return true;
      }
    } else {
      dispatch({
        type: SET_PRODUCT_EVENTS,
        payload: [],
      });
    }
  };

  const handleInventoryModalCancel = () => {
    console.log('❌ Inventory navigation cancelled');
    setShowInventoryModal(false);
    setInventoryNextLocation(null);
  };

  const handleInventoryModalLeave = () => {
    console.log(
      '✅ Leaving inventory, navigating to:',
      inventoryNextLocation?.pathname
    );
    setShowInventoryModal(false);
    if (inventoryNextLocation) {
      // Add bypass flag to prevent blocking again
      setTimeout(() => {
        history.push({
          pathname: inventoryNextLocation.pathname,
          search: inventoryNextLocation.search,
          state: { bypassInventoryBlock: true },
        });
      }, 100);
    }
  };

  useEffect(() => {
    const excludePaths = [
      '/signup/accountInfo',
      '/signup/legalInfo',
      '/signup/billingInfo',
      '/location/invitation',
    ];

    if (
      !excludePaths.some((path) => history.location.pathname.includes(path))
    ) {
      removeItemAfterSignUp();
    }
  }, [history]);

  useEffect(() => {
    if ((isBulked || hasInventoryUnsavedChanges) && !history.location.state) {
      const unblock = history.block(historyBlock);
      return () => {
        unblock();
      };
    }
  }, [
    history,
    isBulked,
    hasInventoryUnsavedChanges,
    history.location.pathname,
    history.location.search,
    historyBlock,
    menu?.categories,
    menu?.modifiersAll,
  ]);

  useEffect(() => {
    if (productEvents?.modalStatus === ModalOpenStatusEnum.confirm) {
      if (
        (nextLoc && !nextLoc.includes('/menu/overview')) ||
        (!mainMenu?.menuAvailable && nextLoc && !nextLoc?.includes('menu'))
      ) {
        if (!nextLoc.includes('/menu')) {
          window.location.pathname = nextLoc;
        } else {
          history.push(nextLoc);
          window.location.reload();
        }
      }
    }
  }, [nextLoc, productEvents?.modalStatus]);

  return (
    <GoogleTagWidget>
      {authorized ? (
        <>
          {linked && productEvents.eventData[0]?.tag === 'unpushedChanges' && (
            <ConfirmProductActions formData={null} handleSubmit={() => {}} />
          )}
          <UnpublishedInventoryChangesModal
            open={showInventoryModal}
            onCancel={handleInventoryModalCancel}
            onLeave={handleInventoryModalLeave}
          />
          <Main />
        </>
      ) : (
        <Auth />
      )}
    </GoogleTagWidget>
  );
}

export default App;
